import { useAuth } from '@/contexts/AuthContext';
import { useBusinessPermissions } from '@/hooks/useBusinessPermissions';
import { useAdminPermissions } from '@/hooks/useAdminPermissions';
import { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { AlertTriangle } from 'lucide-react';

interface ProtectedRouteProps {
  children: ReactNode;
  feature: 'admin' | 'businessAdmin' | 'metaDashboard';
}

export function ProtectedRoute({ children, feature }: ProtectedRouteProps) {
  const { user, loading } = useAuth();
  const { isAdmin } = useAdminPermissions();
  const { isBusinessUser } = useBusinessPermissions();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  // Check feature access
  const hasAccess = (() => {
    switch (feature) {
      case 'admin':
        return isAdmin;
      case 'businessAdmin':
        return isBusinessUser;
      case 'metaDashboard':
        return isAdmin || isBusinessUser; // Both can access analytics
      default:
        return false;
    }
  })();

  if (!hasAccess) {
    const getAccessMessage = () => {
      switch (feature) {
        case 'admin':
          return 'Necesitas permisos de administrador para acceder a esta sección.';
        case 'businessAdmin':
          return 'Necesitas una cuenta Business o Enterprise para acceder al panel empresarial.';
        case 'metaDashboard':
          return 'Necesitas permisos especiales para acceder al Meta Dashboard.';
        default:
          return 'No tienes permisos para acceder a esta sección.';
      }
    };

    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <AlertTriangle className="w-12 h-12 text-destructive mx-auto mb-4" />
            <CardTitle>Acceso Denegado</CardTitle>
            <CardDescription>
              {getAccessMessage()}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button className="w-full" onClick={() => window.history.back()}>
              Volver
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return <>{children}</>;
}